import {
  MjmlColumn,
  MjmlGroup,
  MjmlSection,
  MjmlText,
} from "@faire/mjml-react";
import { InteractiveCartDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/trackable-types";
import {
  CartLayoutType,
  EmailFormat,
  getLayoutTypeFromColumnCount,
  ProductSelectionType,
  Section,
  SharedProps,
  Size,
} from "@redotech/redo-model/email-builder";
import { memo } from "react";
import { LineItemButton } from ".";
import { StaticRecommendedProduct } from "../../../product-recommendations";
import { NestedEmailButton } from "../button";
import { EmailLineItem } from "../email-line-item";
import { BUTTON_SCHEMA_FIELD_NAME } from "./amp-cart";

export type InteractiveCartProps = Section.InteractiveCart & SharedProps;
export const StaticEmailInteractiveCart = memo(
  function StaticEmailInteractiveCart(
    props: InteractiveCartProps & {
      format: EmailFormat.STATIC;
      staticRecommendedProducts: StaticRecommendedProduct[];
    },
  ) {
    if (!props.schemaFieldName) throw new Error("schemaFieldName is required");

    const interactiveCart = props.schemaInstance[props.schemaFieldName] as
      | InteractiveCartDataType["Interactive Cart"]
      | undefined;

    if (!interactiveCart?.cart) {
      return null;
    }
    if (!props.staticRecommendedProducts) {
      return null;
    }
    const buttonSchemaInstance = { url: interactiveCart.cart.checkoutUrl };
    const customerHadExistingCart = !interactiveCart.cart.generatedByRedo;
    const padding = props.sectionPadding;

    return (
      <>
        <StaticLineItems {...props} hasExistingCart={customerHadExistingCart} />
        <MjmlSection
          backgroundColor={props.sectionColor}
          paddingBottom={padding.bottom}
          paddingLeft={padding.left}
          paddingRight={padding.right}
        >
          {customerHadExistingCart && (
            <NestedEmailButton
              {...props.checkoutButton}
              as="a"
              buttonLink={interactiveCart.cart.checkoutUrl}
              format={props.format}
              fullWidth
              horizontalPadding={Size.CUSTOM}
              isBuilder={props.isBuilder}
              padding={{
                ...props.checkoutButton.padding,
                top: props.checkoutButton.padding.top || 24,
              }}
              schemaFieldName={BUTTON_SCHEMA_FIELD_NAME}
              schemaInstance={buttonSchemaInstance}
              sectionColor="transparent"
              team={props.team}
              template={props.template}
              utm={props.utm}
              verticalPadding={Size.CUSTOM}
            />
          )}
        </MjmlSection>
      </>
    );
  },
);

const MaybeMjmlGroup = ({
  responsive,
  children,
}: {
  responsive: boolean;
  children: React.ReactNode;
}) => (responsive ? children : <MjmlGroup>{children}</MjmlGroup>);

const StaticLineItems = memo(function StaticLineItems(
  cartProps: InteractiveCartProps & {
    format: EmailFormat.STATIC;
    staticRecommendedProducts: StaticRecommendedProduct[];
    hasExistingCart: boolean;
  },
) {
  const items =
    cartProps.productSelectionType === ProductSelectionType.DYNAMIC
      ? cartProps.staticRecommendedProducts.slice(
          0,
          Math.max(
            cartProps.numberOfProducts,
            cartProps.staticRecommendedProducts.filter((p) => p.alreadyInCart)
              .length,
          ),
        )
      : cartProps.staticRecommendedProducts;

  const rows = [];
  for (let i = 0; i < items.length; i += cartProps?.columns || 1) {
    rows.push(items.slice(i, i + (cartProps?.columns || 1)));
  }

  const layoutType = getLayoutTypeFromColumnCount(cartProps.columns);

  return (
    <>
      {rows.map((rowItems, rowIndex) => (
        <MjmlSection
          backgroundColor={cartProps.sectionColor}
          key={rowIndex}
          paddingLeft={cartProps.sectionPadding.left}
          paddingRight={cartProps.sectionPadding.right}
        >
          <MaybeMjmlGroup responsive={cartProps.stackOnMobile}>
            {rowItems.map((item, colIndex) => {
              // item.pdpUrl is occasionally null, but we can infer it
              const itemUrl =
                item?.pdpUrl ||
                (item.handle
                  ? `${cartProps.team.storeUrl}/products/${item.handle}`
                  : undefined);
              return (
                <MjmlColumn
                  key={colIndex}
                  padding={layoutType === CartLayoutType.ROWS ? 0 : "12px"}
                >
                  <MjmlText
                    color={cartProps.textColor}
                    fontFamily={cartProps.fontFamily}
                  >
                    <EmailLineItem
                      alignment={cartProps.alignment}
                      format={cartProps.format}
                      imageCornerRadius={cartProps.imageCornerRadius}
                      imageSize={cartProps.imageSize}
                      isBuilder={cartProps.isBuilder}
                      item={{
                        ...item,
                        quantity: item.quantity,
                        properties: {},
                      }}
                      showDummyImages={
                        cartProps.isBuilder &&
                        cartProps.productSelectionType ===
                          ProductSelectionType.DYNAMIC
                      }
                      showImage={cartProps.showImage ?? true}
                      showPrice={cartProps.showPrice ?? true}
                      showQuantity={
                        cartProps.showQuantity ?? cartProps.hasExistingCart
                      }
                      showTitle={cartProps.showTitle ?? true}
                      showVariant={false}
                      stackType={layoutType}
                      team={cartProps.team}
                      textColor={cartProps.textColor}
                      utm={cartProps.utm}
                    >
                      {!item.alreadyInCart &&
                        (cartProps.showButton ?? true) && (
                          <LineItemButton
                            {...cartProps.lineItemButtons}
                            alignment={
                              layoutType === CartLayoutType.COLUMNS
                                ? cartProps.alignment
                                : cartProps.lineItemButtons.alignment
                            }
                            as="a"
                            buttonLink={
                              itemUrl
                                ? cartProps.utm.applyToUrl(itemUrl)
                                : undefined
                            }
                            buttonText="Add to Cart"
                            format={cartProps.format}
                            isBuilder={cartProps.isBuilder}
                            noPadding={layoutType === CartLayoutType.COLUMNS}
                            schemaInstance={cartProps.schemaInstance}
                            team={cartProps.team}
                            template={cartProps.template}
                            utm={cartProps.utm}
                          />
                        )}
                    </EmailLineItem>
                  </MjmlText>
                </MjmlColumn>
              );
            })}
            {/* Potentially pad the last row with empty columns to ensure it looks like a grid */}
            {cartProps.columns &&
              rowItems.length < cartProps.columns &&
              [...Array(cartProps.columns - rowItems.length)].map((_, i) => (
                <MjmlColumn key={i} padding="12px">
                  <MjmlText />
                </MjmlColumn>
              ))}
          </MaybeMjmlGroup>
        </MjmlSection>
      ))}
    </>
  );
});
